<template>
  <tr :class="rowClass">
    <td class="text-center align-middle">
      <input
        type="checkbox"
        :checked="row.selected"
        :disabled="hasError"
        :title="hasError ? 'Dòng còn lỗi, sửa xong mới import được' : ''"
        @change="$emit('toggle', $event.target.checked)"
      />
    </td>
    <td class="text-center align-middle">{{ row.number }}</td>
    <td>
      <input
        class="form-control form-control-sm"
        :class="{ 'is-invalid': isInvalid('name') }"
        :value="row.name"
        placeholder="Họ tên"
        @input="update('name', $event.target.value)"
      />
    </td>
    <td>
      <input
        class="form-control form-control-sm"
        :class="{ 'is-invalid': isInvalid('phoneNumber') }"
        :value="row.phoneNumber"
        maxlength="10"
        placeholder="0912345678"
        @input="update('phoneNumber', $event.target.value)"
      />
    </td>
    <td>
      <input
        class="form-control form-control-sm"
        :class="{ 'is-invalid': isInvalid('patientCode') }"
        :value="row.patientCode"
        placeholder="Để trống nếu không có"
        @input="update('patientCode', $event.target.value)"
      />
    </td>
    <td>
      <input
        class="form-control form-control-sm w-80"
        :class="{ 'is-invalid': isInvalid('birthYear') }"
        :value="row.birthYear"
        maxlength="4"
        placeholder="1980"
        @input="update('birthYear', $event.target.value)"
      />
    </td>
    <td>
      <select
        class="form-control form-control-sm w-90"
        :value="row.gender"
        @change="update('gender', castNumber($event.target.value))"
      >
        <option :value="null">--</option>
        <option v-for="opt in genderOpts" :key="opt.value" :value="opt.value">
          {{ opt.text }}
        </option>
      </select>
    </td>
    <td>
      <select
        class="form-control form-control-sm w-90"
        :value="row.title"
        @change="update('title', castNumber($event.target.value))"
      >
        <option :value="null">--</option>
        <option v-for="opt in titleOpts" :key="opt.value" :value="opt.value">
          {{ opt.text }}
        </option>
      </select>
    </td>
    <td>
      <input
        class="form-control form-control-sm"
        :value="row.contactSource"
        placeholder="Nguồn"
        @input="update('contactSource', $event.target.value)"
      />
    </td>
    <td>
      <select
        class="form-control form-control-sm"
        :class="{ 'is-invalid': isInvalid('diseaseType') }"
        :value="row.diseaseType"
        @change="update('diseaseType', $event.target.value || null)"
      >
        <option :value="null">-- Chọn --</option>
        <option v-for="opt in diseaseOpts" :key="opt.value" :value="opt.value">
          {{ opt.text }}
        </option>
      </select>
    </td>
    <td>
      <select
        class="form-control form-control-sm"
        :class="{ 'is-invalid': isInvalid('packageId') }"
        :value="row.packageId"
        @change="update('packageId', $event.target.value || null)"
      >
        <option :value="null">-- Chọn gói --</option>
        <option v-for="pkg in packages" :key="pkg.id" :value="pkg.id">
          {{ pkg.code }} - {{ pkg.name }}
        </option>
      </select>
    </td>
    <td>
      <select
        class="form-control form-control-sm"
        :class="{ 'is-invalid': isInvalid('coachId') }"
        :value="row.coachId"
        @change="update('coachId', $event.target.value || null)"
      >
        <option :value="null">-- Chọn coach --</option>
        <option v-for="coach in coaches" :key="coach.value" :value="coach.value">
          {{ coach.text }}
        </option>
      </select>
    </td>
    <td>
      <!-- Chuỗi tự do, admin gõ tay được ngay ở đây nếu file để trống -->
      <input
        type="text"
        class="form-control form-control-sm"
        placeholder="Nhóm onboard"
        :value="row.onboardGroup"
        @input="update('onboardGroup', $event.target.value || null)"
      />
    </td>
    <td class="align-middle">
      <div class="status" :class="hasError ? 'inactive' : 'active'">
        {{ hasError ? 'Lỗi' : 'Hợp lệ' }}
      </div>
    </td>
    <td class="align-middle">
      <div v-for="(err, idx) in allErrors" :key="idx" class="error-line">
        - {{ err }}
      </div>
    </td>
  </tr>
</template>

<script>
import { DISEASES_ENG } from '../CustomerReceiveEnum';

export default {
  name: 'ImportPreviewRow',
  props: {
    row: {
      type: Object,
      required: true,
    },
    packages: {
      type: Array,
      default: () => [],
    },
    coaches: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      genderOpts: [
        { text: 'Nam', value: 1 },
        { text: 'Nữ', value: 2 },
        { text: 'Khác', value: 0 },
      ],
      titleOpts: [
        { text: 'Cô', value: 0 },
        { text: 'Chú', value: 1 },
        { text: 'Anh', value: 2 },
        { text: 'Chị', value: 3 },
        { text: 'Em', value: 4 },
      ],
      // Value phải là tên enum DiabetesStates, không phải chuỗi tiếng Việt như form tạo mới
      diseaseOpts: Object.entries(DISEASES_ENG).map(([value, text]) => ({
        value,
        text,
      })),
    };
  },
  computed: {
    allErrors() {
      return [...(this.row.localErrors || []), ...(this.row.errorMessage || [])];
    },
    hasError() {
      return this.allErrors.length > 0;
    },
    rowClass() {
      if (this.hasError) return 'row-error';
      if (!this.row.selected) return 'row-skipped';
      return null;
    },
  },
  methods: {
    update(field, value) {
      this.$emit('update', { field, value });
    },
    castNumber(value) {
      if (value === '' || value === null || value === 'null') return null;
      const num = Number(value);
      return Number.isNaN(num) ? null : num;
    },
    // Chỉ tô đỏ ô nào FE bắt được lỗi; lỗi từ BE hiển thị ở cột chi tiết
    isInvalid(field) {
      return (this.row.invalidFields || []).includes(field);
    },
  },
};
</script>

<style lang="scss" scoped>
.row-error {
  background: #fff5f5;
}

.row-skipped {
  opacity: 0.55;
}

.error-line {
  font-size: 12px;
  color: #ff5756;
  white-space: nowrap;
}

.form-control-sm {
  min-width: 110px;
}

.w-80 {
  min-width: 70px;
  width: 70px;
}

.w-90 {
  min-width: 85px;
  width: 85px;
}

.is-invalid {
  border-color: #ff5756;
}


.status {
  padding: 4px 10px;
  border-radius: 20px;
  color: #fff;
  text-align: center;
  white-space: nowrap;

  &.active {
    background: #21a567;
  }

  &.inactive {
    background: #ff5756;
  }
}
</style>
